import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Stack, Typography, Modal, styled, Box, Grid } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormField } from "@/components/elements/FormField";
import { Form } from "@/components/elements/Form";
import { Checkbox } from "@/components/elements/Checkbox";
import { Textarea } from "@/components/elements/Textarea";
import { IconActionDelete, IconClose, IconSave } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { InputTimePicker } from "@/components/elements/InputTimePicker";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { useCaseScheduleSchema } from "@/features/case/schemas/caseScheduleSchema";
import { Talent, TCaseScheduleForm, InterviewMethod } from "@/features/case/types/case";
import { dayjs } from "@/utils/dayjs";
import { TalentSelectDropdown } from "@/components/elements/TalentSelectDropdown";
import { InnerBox } from "@/components/elements/InnerBox";
import { useEnum } from "@/api/services/global/enum";

export type TScheduleAdjustmentModalProps = {
  modal: UseVisibleReturn;
  talents?: Talent[];
  defaultValues?: Partial<TCaseScheduleForm>;
  onSubmit?: (data: TCaseScheduleForm) => void | Promise<void>;
  onDelete?: () => void;
};

export const ScheduleAdjustmentModal: FCC<TScheduleAdjustmentModalProps> = ({
  modal,
  talents = [],
  defaultValues,
  onSubmit: onSubmitHandler,
  onDelete: onDeleteHandler,
}) => {
  const [isSubmit, setIsSubmit] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isConfirmLeaveOpen, setIsConfirmLeaveOpen] = useState(false);
  const { caseScheduleSchema } = useCaseScheduleSchema();

  const { data: interviewMethodOptions = [] } = useEnum("EInterviewMethod");
  const { data: meetingToolTypeOptions = [] } = useEnum("EMeetingToolType");

  const methods = useForm<TCaseScheduleForm>({
    defaultValues: {
      dateTimeTalent: "",
      isConfirm: false,
      talentIds: [],
    },
    resolver: yupResolver(caseScheduleSchema),
    mode: "all",
  });

  const {
    control,
    reset,
    watch,
    setValue,
    trigger,
    clearErrors,
    getValues,
    formState: { isDirty },
  } = methods;
  const isConfirm = watch("isConfirm");
  const selectedTalentIds = watch("talentIds") || [];
  const dateConfirm = watch("dateConfirm");
  const startTimeConfirm = watch("startTimeConfirm");
  const interviewMethod = watch("interviewMethod");
  const isOnlineInterview = Number(interviewMethod) === InterviewMethod.ONLINE;

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isConfirm) {
      clearErrors("dateTimeTalent");
    } else {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      trigger("dateTimeTalent");
      setValue("url", null);
      setValue("interviewMethod", null);
      setValue("meetingToolType", null);
      setValue("meetingId", null);
      setValue("passcode", null);
    }
  }, [isConfirm, trigger, clearErrors, setValue]);

  useEffect(() => {
    if (Number(interviewMethod) === InterviewMethod.OFFLINE) {
      setValue("url", null);
      setValue("meetingToolType", null);
      setValue("meetingId", null);
      setValue("passcode", null);
    }
  }, [interviewMethod, setValue]);

  useEffect(() => {
    if (isConfirm && startTimeConfirm) {
      trigger("startTimeConfirm");
    }
  }, [dateConfirm, isConfirm, startTimeConfirm, trigger]);

  const isEditMode = !!defaultValues;

  // Check if datetime has changed in edit mode
  const hasDateTimeChanged = useMemo(() => {
    if (!isEditMode) return true; // Create mode - always validate as future
    const formValues = getValues();
    const dateChanged = dateConfirm !== formValues.originalDateConfirm;
    const timeChanged = startTimeConfirm !== formValues.originalStartTimeConfirm;
    return dateChanged || timeChanged;
  }, [isEditMode, dateConfirm, startTimeConfirm, getValues]);

  // Only enforce minDate when creating or when datetime has changed in edit mode
  const minDateConstraint = useMemo(() => {
    if (!isEditMode || hasDateTimeChanged) {
      return dayjs();
    }
    return undefined;
  }, [isEditMode, hasDateTimeChanged]);

  const minTimeForToday = useMemo(() => {
    if (isEditMode && !hasDateTimeChanged) {
      return undefined;
    }

    if (!dateConfirm) return undefined;
    const selectedDate = dayjs(dateConfirm);
    const today = dayjs().startOf("day");
    if (selectedDate.isSame(today, "day")) {
      return dayjs();
    }
    return undefined;
  }, [dateConfirm, isEditMode, hasDateTimeChanged]);

  useEffect(() => {
    if (modal.visible) {
      isFirstRender.current = true;
      if (defaultValues) {
        const normalizedDefaultValues = {
          ...defaultValues,
          talentIds: defaultValues.talentIds?.map((id) => Number(id)) || [],
          isConfirm: Boolean(defaultValues.isConfirm ?? false),
          originalDateConfirm: defaultValues.dateConfirm ?? null,
          originalStartTimeConfirm: defaultValues.startTimeConfirm ?? null,
        };
        reset(normalizedDefaultValues, { keepErrors: false });

        setTimeout(() => {
          clearErrors("dateConfirm");
          clearErrors("startTimeConfirm");

          if (normalizedDefaultValues.isConfirm) {
            trigger(["dateConfirm", "startTimeConfirm"]);
          }
        }, 0);
      } else {
        reset({
          dateTimeTalent: "",
          isConfirm: false,
          talentIds: [],
        });
      }
    }
  }, [modal.visible, defaultValues, reset, clearErrors, trigger]);

  const handleCloseAttempt = () => {
    if (isDirty) {
      setIsConfirmLeaveOpen(true);
    } else {
      onClose();
    }
  };

  const onClose = () => {
    setIsSubmit(false);
    setIsConfirmLeaveOpen(false);
    modal.close();
  };

  const handleConfirmLeave = () => {
    onClose();
  };

  const handleCancelLeave = () => {
    setIsConfirmLeaveOpen(false);
  };

  const onSubmit = async (data: TCaseScheduleForm) => {
    try {
      setIsSubmit(true);
      await onSubmitHandler?.(data);
      reset();
      modal.close();
    } catch (error) {
    } finally {
      setIsSubmit(false);
    }
  };

  const handleOpenDeleteConfirm = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setIsDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = () => {
    onDeleteHandler?.();
    setIsDeleteConfirmOpen(false);
    onClose();
  };

  const handleTalentChange = useCallback(
    (newTalentIds: number[]) => {
      setValue("talentIds", newTalentIds, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  const talentOptions = useMemo(
    () =>
      talents
        .filter((candidate) => candidate?.id)
        .map((candidate) => ({
          id: Number(candidate.id),
          fullName: candidate?.fullName ?? "",
          nickname: candidate?.nickname ?? "",
          isRecommendedTalent: candidate?.isRecommendedTalent ?? false,
        })),
    [talents],
  );

  const savedTalentIds = useMemo(() => {
    return defaultValues?.talentIds?.map((id) => Number(id)) || [];
  }, [defaultValues?.talentIds]);

  return (
    <Modal
      open={modal.visible}
      hideBackdrop={false}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 0,
        "& .MuiBackdrop-root": {
          padding: 0,
        },
      }}
    >
      <ModalContainer>
        <FormWrapper>
          <Form methods={methods} onSubmit={(data) => onSubmit(data)} schema={caseScheduleSchema}>
            {/* Header - Fixed */}
            <Stack
              direction="row"
              bgcolor={background.disable}
              height="56px"
              alignItems="center"
              justifyContent="space-between"
              sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3, flexShrink: 0 }}
            >
              <Typography variant="body14Light" color={"#0B0C0D"}>
                面接日程調整
              </Typography>
              <IconBox onClick={handleCloseAttempt} sx={{ p: 0 }}>
                <IconClose color={black[80]} fontSize={24} />
              </IconBox>
            </Stack>

            <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
              <Grid container sx={{ p: 4, pt: 3 }} rowSpacing={1}>
                <Grid item xs={12}>
                  <FormField control={control} name="dateTimeTalent" required={!isConfirm}>
                    <Textarea minRows={6} />
                  </FormField>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="cap12" color={text.primary}>
                    マッチング人材
                  </Typography>

                  <Box sx={{ mt: 0.5 }}>
                    <TalentSelectDropdown
                      talents={talentOptions}
                      selectedIds={selectedTalentIds.map((id) => Number(id))}
                      savedIds={savedTalentIds}
                      onChange={handleTalentChange}
                      placeholder="選択してください"
                      disabled={talents.length === 0}
                      showConfirmedBelow
                    />
                    {talents.length === 0 && (
                      <Typography variant="body14" color={black[70]} sx={{ mt: 1 }}>
                        候補者がありません
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <InnerBox mt={3} showDivider={false} width={"100%"}>
                  <Grid container rowSpacing={2} columnSpacing={3}>
                    <Grid item xs={12}>
                      <FormField control={control} name="isConfirm" noLabel>
                        <Checkbox label="確定した面接日時・オンラインURLなどを入力" checked={!!isConfirm} />
                      </FormField>
                    </Grid>

                    {isConfirm && (
                      <>
                        <Grid item xs={6}>
                          <FormField control={control} name="dateConfirm" required={isConfirm}>
                            <InputDatePicker minDate={minDateConstraint} />
                          </FormField>
                        </Grid>

                        <Grid item xs={6}>
                          <FormField control={control} name="startTimeConfirm" required={isConfirm} label="面接開始時間">
                            <InputTimePicker minTime={minTimeForToday} />
                          </FormField>
                        </Grid>

                        <Grid item xs={12}>
                          <FormField control={control} name="interviewMethod" label="実施方法">
                            <RadioGroup options={interviewMethodOptions} />
                          </FormField>
                        </Grid>

                        {isOnlineInterview && (
                          <>
                            <Grid item xs={4}>
                              <FormField control={control} name="meetingToolType" label="オンライン会議ツール">
                                <Select options={meetingToolTypeOptions} clearable />
                              </FormField>
                            </Grid>

                            <Grid item xs={4}>
                              <FormField control={control} name="meetingId" label="ミーティングID">
                                <Input placeholder="入力してください" />
                              </FormField>
                            </Grid>

                            <Grid item xs={4}>
                              <FormField control={control} name="passcode" label="パスコード">
                                <Input placeholder="入力してください" />
                              </FormField>
                            </Grid>

                            <Grid item xs={12}>
                              <FormField control={control} name="url">
                                <Input placeholder="面接URLを入力してください" />
                              </FormField>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </Grid>
                </InnerBox>
              </Grid>
            </Box>

            <Box sx={{ flexShrink: 0 }}>
              <hr color={divider.high} />
              <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px">
                <Button variant="ghost" size="sm" onClick={handleCloseAttempt} sx={{ width: "102px" }}>
                  キャンセル
                </Button>
                <Stack direction="row" gap="16px">
                  {isEditMode && (
                    <Button variant="danger" size="sm" color="error" onClick={handleOpenDeleteConfirm} startIcon={<IconActionDelete />}>
                      スケジュールを削除
                    </Button>
                  )}
                  <Button variant="primary" size="sm" type="submit" disabled={isSubmit} startIcon={<IconSave />} sx={{ width: "88px" }}>
                    保存
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Form>
        </FormWrapper>

        <ConfirmDeleteModal
          open={isDeleteConfirmOpen}
          onClose={handleCloseDeleteConfirm}
          onOk={handleConfirmDelete}
          message="このスケジュールを削除してもよろしいですか？"
        />

        <ConfirmSaveModal open={isConfirmLeaveOpen} onClose={handleCancelLeave} onOk={handleConfirmLeave} enableFooter>
          <Typography>入力が完了していません。このまま戻ってもよろしいですか？</Typography>
        </ConfirmSaveModal>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 1100px;
  max-height: 90vh;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  margin: auto;
  overflow: hidden;
  &:focus {
    outline: none;
  }
`;

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  & > form {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
`;
